import React, { useState, useEffect } from 'react';
import { RefreshCw, Info, XCircle, Loader, Database } from 'lucide-react';
import { formatDate } from '../lib/utils';

const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:3000';

const SidebarDatabaseStatus = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dbStatus, setDbStatus] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        fetchDatabaseStatus();
    }, []);

    const fetchDatabaseStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/status`, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch database status');
            }

            const data = await response.json();
            setDbStatus(data);
            setError(null);
        } catch (err) {
            setError('Failed to load database status');
            console.error('Error fetching database status:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center text-gray-500">
                    <Loader size={16} className="animate-spin mr-2" />
                    <span className="text-xs">Loading DB status...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center text-red-500">
                    <XCircle size={16} className="mr-2" />
                    <span className="text-xs">DB Status Error</span>
                </div>
            </div>
        );
    }

    if (!dbStatus) return null;

    return (
        <div className="border-t border-gray-200">
            <div 
                className="px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <Database size={16} className="mr-2 text-gray-500" />
                        <div>
                            <div className="text-xs font-medium text-gray-700">Database</div>
                            <div className="flex items-center">
                                <span className={`w-2 h-2 rounded-full mr-1 ${dbStatus.database.isAvailable ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                <span className="text-xs text-gray-500">v{dbStatus.database.version}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                fetchDatabaseStatus();
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Refresh"
                        >
                            <RefreshCw size={12} />
                        </button>
                    </div>
                </div>
            </div>
            
            {isExpanded && (
                <div className="px-4 pb-3 text-xs text-gray-600 space-y-2">
                    <div>Updated: {formatDate(dbStatus.database.lastUpdated)}</div>
                    <div className="space-y-1">
                        {Object.entries(dbStatus.database.datasets).map(([key, dataset]) => (
                            <div key={key} className="flex justify-between">
                                <span className="capitalize">{key}:</span>
                                <span>{dataset.count.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center text-gray-500">
                        <Info size={10} className="mr-1" />
                        <span>Updated monthly</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SidebarDatabaseStatus;
